import { env, webConfig } from '@gitbook/env';
import { generateHashFromUrl, getUrlFileExtension, parseUrl } from '@gitbook/util-url';

import { getStorageCachedURL } from './cache';

export interface StorageImageResizingOptions {
    dpr?: number;
    width?: number;
    height?: number;
    fit?: 'scale-down' | 'contain' | 'cover' | 'crop' | 'pad';
    onerror?: 'redirect';
}

export const DEFAULT_IMAGE_RESIZING_OPTIONS: StorageImageResizingOptions = {
    dpr: 2,
    width: 256,
    onerror: 'redirect',
};

/**
 * Compute a url to a resized version of an image.
 * It uses Cloudflare Image Resizing (https://developers.cloudflare.com/images/url-format).
 * It also automatically use the cached version of the file.
 */
export function getStorageImageResizedURL(
    inputUrl: string,
    options: StorageImageResizingOptions = DEFAULT_IMAGE_RESIZING_OPTIONS
): string {
    if (['localhost', '127.0.0.1'].includes(new URL(inputUrl).hostname)) {
        return inputUrl;
    }

    // If the image URL is already a Cloudflare URL, we extract the original URL
    // and use it with the new resizing parameters.
    const parsed = new URL(inputUrl);
    if (parsed.hostname === webConfig.cloudflare.zoneDomain) {
        const originalUrl = decodeURIComponent(parsed.pathname.split('/').pop() || '');
        // If we couldn't parse an URL, it's not a Cloudflare URL, just return the original URL
        if (!parseUrl(originalUrl)) {
            return inputUrl;
        }
        return getStorageImageResizedURL(originalUrl, options);
    }

    // The url could now also be from the images service
    if (parsed.hostname === webConfig.services.images.host) {
        const originalUrl = decodeURIComponent(parsed.pathname.split('/').pop() || '');
        // If we couldn't parse an URL, it's not a valid images service, just return the original URL
        if (!parseUrl(originalUrl)) {
            return inputUrl;
        }
        return getStorageImageResizedURL(originalUrl, options);
    }
    const cachedUrl = getStorageCachedURL(inputUrl);
    if (!webConfig.cloudflare.zoneDomain) {
        return cachedUrl;
    }

    const fileExtension = getUrlFileExtension(cachedUrl);
    // Cloudflare doesn't support SVG or AVIF, just return the Storage URL
    if (!fileExtension || fileExtension === '.svg' || fileExtension === '.avif') {
        return cachedUrl;
    }

    // We need to fallback to the old cdn-cgi endpoint for now in tests.
    // We'll switch them to the staging endpoint probably once it is deployed.
    if (env === 'test') {
        return `https://${webConfig.cloudflare.zoneDomain}/cdn-cgi/image/${stringifyOptions(
            options,
            cachedUrl
        )}/${encodeURIComponent(cachedUrl)}`;
    }

    return `https://${webConfig.services.images.host}/__img/${stringifyOptions(
        options,
        cachedUrl
    )}/${encodeURIComponent(cachedUrl)}`;
}

function stringifyOptions(options: StorageImageResizingOptions, url: string): string {
    const signature = generateHashFromUrl(url, webConfig.storage.imageSalt);

    return Object.entries({ ...options, format: 'auto', signature }).reduce(
        (rest, [key, value]) => {
            if (value === undefined) {
                return rest;
            }

            return `${rest}${rest ? ',' : ''}${key}=${value}`;
        },
        ''
    );
}
